
import { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, Grid, IconButton, InputBase, InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow, Typography } from '@mui/material';

import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';

import { TablePaginationActions } from '../helpers/TablePaginationActions';

import { usePages } from '../hooks/usePages';

import { openSnackbar } from "../store/slices/SnackbarSlice";

import { DialogNuevaPersona } from './DialogNuevaPersona';
import { DialogBuscarPersona } from './DialogBuscarPersona';

import { TableTheadStyle, TablePaddingStyle } from '../styles/TableStyle';
import { DialogActionsStyle, DialogContentStyle } from '../styles/DialogStyle';
import { InputSearchButtonsStyle, InputSearchStyle } from '../styles/FormStyle';

import { ObtenerTipoParte } from '../connections/catalogos/TipoParteConnection';
import { ObtenerPartesExhortoRecibido, AgregarPartesExhortoRecibido } from '../connections/recepcion/ExhortoRecibidoConnection';

import { ParteProps } from '../interfaces/recepcion/ParteInterface';
import { TipoParteProps } from '../interfaces/catalogos/TipoParteInterface';
import { defaultPersonaBusqueda, PersonaBusquedaProps } from '../interfaces/comun/PersonaInterface';

interface Props {
    openDialog:         boolean;
    handleCloseDialog:  () => void;
    idExhortoRecibido?: number;
    onGuardar?:         ( actores: string[], demandados: string[] ) => void;
}

export const DialogPartesExhortoRecibido = ( { openDialog, handleCloseDialog, idExhortoRecibido = 0, onGuardar }: Props ) => {

    const dispatch = useDispatch();

    const [tipoParte, setTipoParte] = useState( 0 );
    const [tipoParteArray, setTipoParteArray] = useState<TipoParteProps[]>( [] );

    const [loading, setLoading] = useState( true );
    const [loadingGuardar, setLoadingGuardar] = useState( false );
    const [openConfirmacion, setOpenConfirmacion] = useState( false );

    const [openDialogNuevaPersona, setOpenDialogNuevaPersona] = useState( false );
    const [openDialogBuscarPersona, setOpenDialogBuscarPersona] = useState( false );

    const [idReferencia, setIdReferencia] = useState( 0 );

    const [rows, setRows] = useState<ParteProps[]>( [] );

    const [{ idPersona, nombreCompleto, idTipoPersona }, setPersona] = useState<PersonaBusquedaProps>( defaultPersonaBusqueda );

    const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePages( 5 );

    const seleccionarPersona = ( persona: PersonaBusquedaProps ) => setPersona( persona );

    const handleAgregar = () => {

        const tipoParteData = tipoParteArray.find( (e) => e.idTipoParte === tipoParte );
        const id = new Date().getUTCSeconds() + '' + new Date().getUTCMilliseconds();

        if( idPersona !== 0 ){

            const personaValidate = rows.find( (e) => e.idPersona === idPersona && e.idTipoParte === tipoParteData?.idTipoParte );

            if( !personaValidate ){

                const personaExiste = rows.find( (e) => e.idPersona === idPersona );

                if( personaExiste ){

                    if( ( personaExiste.idTipoParte === 1 && tipoParteData?.idTipoParte === 2 ) ||
                        ( personaExiste.idTipoParte === 2 && tipoParteData?.idTipoParte === 1 ) ){

                        dispatch( openSnackbar({ message: 'La persona no puede agregarse como ' + ( tipoParteData?.descripcion ?? '' ), variant: 'warning' }) );
                        return false;
                    }

                }

                const data: ParteProps = {
                    id: parseInt( id ),
                    idPersona: idPersona,
                    persona: nombreCompleto,
                    idTipoParte: tipoParteData?.idTipoParte ?? 0,
                    tipoParte: tipoParteData?.descripcion ?? '',
                    idTipoPersona: idTipoPersona,
                };

                setRows( [...rows, data] );
            }
            else{
                dispatch( openSnackbar({ message: `La persona ya se encuentra agregada como ${ tipoParteData?.descripcion ?? '' }`, variant: 'warning' }) );
                return false;
            }

        }

        setPersona( defaultPersonaBusqueda );
        setTipoParte( 0 );

    }

    const handleRemover = () => {
        setRows( rows.filter( (e) => e.id !== idReferencia ) );
        setOpenConfirmacion( false );
    }

    const handleGuardar = async () => {

        setLoadingGuardar( true );

        await AgregarPartesExhortoRecibido({ idExhortoRecibido, partes: rows }).then( resp => {

            setTimeout(() => {

                const { success, message } = resp;

                if( success ){

                    const actores    = rows.filter( (r) => r.idTipoParte === 1 ).map( (r) => r.persona );
                    const demandados = rows.filter( (r) => r.idTipoParte === 2 ).map( (r) => r.persona );

                    dispatch( openSnackbar({ message: message, variant: 'success' }) );
                    setLoadingGuardar( false );

                    handleCloseDialog();

                    onGuardar?.( actores, demandados );

                }
                else{

                    dispatch( openSnackbar({ message: message, variant: 'error' }) );
                    setLoadingGuardar( false );

                }

            }, 700);

        });

    }

    useEffect(() => {

        async function Obtener(){
            await ObtenerTipoParte().then( resp => setTipoParteArray( resp ) );
        }

        Obtener();

    }, []);

    useEffect(() => {

        async function Obtener(){

            setLoading( true );
            setRows( [] );

            await ObtenerPartesExhortoRecibido( idExhortoRecibido ).then( resp => {

                setTimeout(() => {
                    setRows( resp );
                    setLoading( false );
                }, 700);

            });

        }

        if( idExhortoRecibido !== 0 && openDialog ){
            Obtener();
        }
        else{
            setLoading( false );
        }

    }, [openDialog, idExhortoRecibido]);

    return (

        <>

            <Dialog
                fullWidth
                open={ openDialog }
                maxWidth='md'
            >

                <DialogTitle>
                    Agregar parte(s)
                </DialogTitle>

                <DialogContent>

                    <Grid container spacing={3} mt={1}>

                        <Grid size={{ xs: 12, md: 8 }}>

                            <Paper component="div" sx={ InputSearchStyle }>

                                <InputBase
                                    sx={{ ml: 1, flex: 1 }}
                                    inputProps={{ 'aria-label': 'Buscar persona' }}
                                    placeholder="Buscar persona"
                                    name="persona"
                                    autoComplete="off"
                                    disabled
                                    value={ nombreCompleto }
                                />

                                <Button
                                    variant='contained'
                                    size='small'
                                    sx={ InputSearchButtonsStyle }
                                    onClick={ () => setOpenDialogBuscarPersona( true ) }
                                >
                                    <SearchIcon />
                                </Button>

                                <Button
                                    variant='contained'
                                    size='small'
                                    sx={ InputSearchButtonsStyle }
                                    onClick={ () => setOpenDialogNuevaPersona( true ) }
                                >
                                    <PersonAddIcon />
                                </Button>

                            </Paper>

                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>

                            <FormControl fullWidth>

                                <InputLabel>Tipo de parte</InputLabel>

                                <Select
                                    label="Tipo de parte"
                                    value={ tipoParte }
                                    onChange={ (e) => setTipoParte( e.target.value as number ) }
                                    disabled={ idPersona === 0 }
                                >
                                    <MenuItem key={0} value={0}>Selecciona una opción</MenuItem>

                                    {
                                        tipoParteArray
                                        .filter( (e) => e.idTipoParte === 1 || e.idTipoParte === 2 )
                                        .map( (e) => (
                                            <MenuItem key={ e.idTipoParte } value={ e.idTipoParte }>{ e.descripcion }</MenuItem>
                                        ))
                                    }

                                </Select>

                            </FormControl>

                        </Grid>

                        <Grid size={{ xs: 12, md: 12 }} textAlign={'end'}>

                            <Button
                                variant="contained"
                                onClick={ handleAgregar }
                                disabled={ tipoParte === 0 || idPersona === 0 }
                            >
                                Agregar
                            </Button>

                        </Grid>

                        <Grid size={{ xs: 12, md: 12 }}>

                            <TableContainer sx={{ borderRadius: 3, mt: 1 }}>

                                <Table sx={{ minWidth: 500 }}>

                                    <TableHead sx={{ ...TableTheadStyle }}>

                                        <TableRow sx={{ borderTopLeftRadius: 10, borderTopRightRadius: 10 }}>

                                            <TableCell style={{ ...TablePaddingStyle, ...TableTheadStyle, width: '50%' }}>Nombre</TableCell>
                                            <TableCell style={{ ...TablePaddingStyle, ...TableTheadStyle, width: '30%' }}>Tipo de parte</TableCell>
                                            <TableCell style={{ ...TablePaddingStyle, ...TableTheadStyle, width: '20%' }}> </TableCell>

                                        </TableRow>

                                    </TableHead>

                                    <TableBody>

                                        {
                                            (
                                                rowsPerPage > 0
                                                ? rows.slice( page * rowsPerPage, page * rowsPerPage + rowsPerPage )
                                                : rows
                                            ).map( ( { id, persona, tipoParte }, index ) => (

                                                <TableRow key={ index }>

                                                    <TableCell>{ persona }</TableCell>
                                                    <TableCell>{ tipoParte }</TableCell>

                                                    <TableCell sx={{ textAlign: 'end' }}>

                                                        <IconButton color="primary" onClick={ () => { setIdReferencia( id ); setOpenConfirmacion( true ); } }>
                                                            <DeleteIcon />
                                                        </IconButton>

                                                    </TableCell>

                                                </TableRow>

                                            ))
                                        }

                                        {
                                            ( rows.length === 0 && !loading )
                                            ?
                                                <TableRow style={{ height: 73 }}>

                                                    <TableCell colSpan={7} align="center">
                                                        <Typography variant="caption" color='secondary'>No se agregaron partes</Typography>
                                                    </TableCell>

                                                </TableRow>
                                            :
                                                loading
                                                &&
                                                    <TableRow style={{ height: 73 }}>

                                                        <TableCell colSpan={7} align="center">
                                                            <CircularProgress size={32} />
                                                        </TableCell>

                                                    </TableRow>
                                        }

                                    </TableBody>

                                    <TableFooter>

                                        <TableRow>

                                            <TablePagination
                                                rowsPerPageOptions={[]}
                                                colSpan={4}
                                                count={ rows.length }
                                                rowsPerPage={ rowsPerPage }
                                                page={ page }
                                                onPageChange={ handleChangePage }
                                                onRowsPerPageChange={ handleChangeRowsPerPage }
                                                ActionsComponent={ TablePaginationActions }
                                                labelDisplayedRows={
                                                    ({ from, to, count }) => `${ from }-${ to } de ${ count }`
                                                }
                                            />

                                        </TableRow>

                                    </TableFooter>

                                </Table>

                            </TableContainer>

                        </Grid>

                    </Grid>

                </DialogContent>

                <DialogActions>

                    <Button color='secondary' disabled={ loadingGuardar } onClick={ handleCloseDialog }>Cancelar</Button>

                    <Button
                        variant="contained"
                        loading={ loadingGuardar }
                        onClick={ handleGuardar }
                    >
                        Guardar
                    </Button>

                </DialogActions>

            </Dialog>

            <DialogNuevaPersona
                openDialog={ openDialogNuevaPersona }
                handleCloseDialog={ () => setOpenDialogNuevaPersona( false ) }
                seleccionarPersona={ seleccionarPersona }
            />

            <DialogBuscarPersona
                openDialog={ openDialogBuscarPersona }
                handleCloseDialog={ () => setOpenDialogBuscarPersona( false ) }
                seleccionarPersona={ seleccionarPersona }
            />

            <Dialog
                maxWidth='xs'
                fullWidth
                disableEscapeKeyDown
                open={ openConfirmacion }
                onClose={ () => {} }
            >

                <DialogTitle>
                    Confirmación
                </DialogTitle>

                <DialogContent sx={ DialogContentStyle }>

                    <DialogContentText sx={{ color: 'rgba(0, 0, 0, 0.8)' }} mt={1}>
                        ¿Desea quitar el registro seleccionado?
                    </DialogContentText>

                </DialogContent>

                <DialogActions sx={ DialogActionsStyle }>

                    <Button color='secondary' onClick={ () => setOpenConfirmacion( false ) }>Cancelar</Button>
                    <Button onClick={ handleRemover } variant='contained'>Aceptar</Button>

                </DialogActions>

            </Dialog>

        </>

    )

}
